import { z } from 'zod'
import { emailSchema } from './common'

const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v)

/**
 * Private links carry their secret in the path or the query string. Reports keep the page they
 * came from without the secret: anyone who could read the report could otherwise use the link.
 */
const TOKEN_PATHS =
  /(\/(?:unsubscribe|email-preferences|email-confirm|marketing\/(?:preferences|resubscribe|unsubscribe)|checkin\/v|api\/v1\/checkin|sign-in\/email)\/)[^/?#\s"'<>]+/gi
// Any parameter whose name is or ends in one of these (e.g. payment_intent_client_secret, api_key).
const TOKEN_PARAMS =
  /([?&;][\w.-]*(?:token|code|state|key|secret|session|email|password)=)[^&#\s"'<>]*/gi

/** Blanks out private tokens (and emails in query strings) wherever a URL appears in `text`. */
export function redactPrivate(text: string): string {
  return text.replace(TOKEN_PATHS, '$1[redacted]').replace(TOKEN_PARAMS, '$1[redacted]')
}

/** "Report a problem": everything but the message is filled in by the page. */
export const problemReportSchema = z.object({
  message: z.preprocess(blankToUndefined, z.string().trim().max(4000).optional()),
  email: z.preprocess(blankToUndefined, emailSchema.optional()),
  pageUrl: z.string().max(2000).optional(),
  errorText: z.string().max(4000).optional(),
  requestId: z.string().max(100).optional(),
  appVersion: z.string().max(100).optional(),
  homepage: z.string().max(200).optional(),
})
export type ProblemReportInput = z.infer<typeof problemReportSchema>

export const PROBLEM_REPORT_STATUSES = ['open', 'resolved'] as const

export interface ProblemReportRow {
  id: string
  pageUrl: string | null
  message: string | null
  errorText: string | null
  requestId: string | null
  userAgent: string | null
  appVersion: string | null
  personId: string | null
  personName: string | null
  contactEmail: string | null
  status: (typeof PROBLEM_REPORT_STATUSES)[number]
  resolvedAt: string | null
  createdAt: string
}
